import { Building2, Globe, Lock, Mail, Plus, Search, Share2, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { SegmentedControl, Switch } from '@/components/ui/controls'
import { Badge } from '@/components/ui/display'
import { Input } from '@/components/ui/field'
import { Popover, PopoverContent, PopoverTrigger, Tooltip } from '@/components/ui/overlays'
import { DEFAULT_LANGUAGE, LANGUAGES } from '@/data/people'
import type { AccessPerson, MeetingAccess, ShareScope } from '@/data/types'
import { cn } from '@/lib/utils'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The armed state of a Share button: what it will do, in which language, and
 * the button that does it.
 *
 * Language sits here rather than in a settings panel because it is only ever
 * decided at the moment of sending, and it is only ever decided once per
 * recipient — the row records which language went out afterwards.
 */
function ConfirmStrip({
  label,
  language,
  onLanguageChange,
  onConfirm,
  onCancel,
  busy,
  className,
}: {
  label: string
  language: string
  onLanguageChange: (language: string) => void
  onConfirm: () => void
  onCancel: () => void
  busy?: boolean
  className?: string
}) {
  return (
    <div className={cn('mt-1.5 flex items-center gap-1.5', className)}>
      <Mail className="size-3 shrink-0 text-[var(--color-fg-subtle)]" />
      <span className="min-w-0 flex-1 truncate text-2xs text-[var(--color-fg-subtle)]">
        {label}
      </span>
      <SegmentedControl
        size="sm"
        value={language}
        onValueChange={onLanguageChange}
        options={LANGUAGES.map((item) => ({ value: item.code, label: item.code.toUpperCase(), title: item.label }))}
      />
      <Button size="xs" variant="primary" disabled={busy} onClick={onConfirm}>
        Send
      </Button>
      <Button size="iconSm" variant="ghost" onClick={onCancel} aria-label="Cancel">
        <X className="size-3" />
      </Button>
    </div>
  )
}

type Scope = Exclude<ShareScope, 'none'>

/** A grant on the whole series also covers this one; the reverse is not true. */
function covers(grant: ShareScope, scope: Scope): boolean {
  if (grant === 'series') return true
  return grant === 'meeting' && scope === 'meeting'
}

function summarize(access: MeetingAccess) {
  const shared = access.people.filter((person) => person.grant !== 'none').length
  if (access.organization !== 'none') {
    return {
      icon: Globe,
      tone: 'strong' as const,
      label: shared > 0 ? `Whole organisation, plus ${shared} shared` : 'Whole organisation',
    }
  }
  if (shared > 0) {
    return {
      icon: Share2,
      tone: 'strong' as const,
      label: `Shared with ${shared} ${shared === 1 ? 'person' : 'people'}`,
    }
  }
  return { icon: Lock, tone: 'muted' as const, label: 'Private' }
}

/** What is waiting on a confirmation, if anything. */
type Pending =
  | { kind: 'person'; email: string; name: string }
  | { kind: 'all'; emails: string[] }
  | null

/**
 * Sharing, as a dropdown that confirms in place.
 *
 * It hangs off the row's icon rather than taking over the screen: sharing is one
 * row's business, and a modal that dims the list makes you leave the thing you
 * were reading down in order to decide who gets it.
 *
 * Granting access SENDS AN EMAIL, though, often to someone outside the firm — so
 * it is still two steps, and the second one happens ON THE ROW: the Share button
 * turns into the language choice and a Send that names what it does. Swapping
 * the whole panel for a confirmation pane, which is what this did before, took
 * the list away to ask about one line of it — and the person you were about to
 * mail was no longer on screen while you decided. Revoking stays one click,
 * because nothing is sent.
 */
export function ShareButton({
  title,
  access,
  isRecurring,
  onOrganizationChange,
  onPersonChange,
  onAddPerson,
  onShareAll,
  busy,
}: {
  title: string
  access: MeetingAccess
  isRecurring: boolean
  onOrganizationChange: (scope: ShareScope) => void
  onPersonChange: (email: string, grant: ShareScope, language?: string) => void
  onAddPerson: (email: string, scope: ShareScope, language: string) => void
  onShareAll: (scope: Scope, language: string) => void
  busy?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [scope, setScope] = useState<Scope>('meeting')
  const [query, setQuery] = useState('')
  const [pending, setPending] = useState<Pending>(null)
  const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE)

  const summary = summarize(access)
  const normalized = query.trim().toLowerCase()
  const orgOn = covers(access.organization, scope)

  const reset = () => {
    setScope('meeting')
    setQuery('')
    setPending(null)
    setLanguage(DEFAULT_LANGUAGE)
  }

  const { internal, external } = useMemo(() => {
    const visible = access.people.filter(
      (person) =>
        !normalized ||
        person.name.toLowerCase().includes(normalized) ||
        person.email.includes(normalized)
    )
    return {
      internal: visible.filter((person) => person.isInternal),
      external: visible.filter((person) => !person.isInternal),
    }
  }, [access.people, normalized])

  const canAdd =
    EMAIL.test(normalized) && !access.people.some((person) => person.email === normalized)

  /** External attendees who would be mailed by "share with everyone". */
  const unshared = access.people.filter(
    (person) => !person.isInternal && person.isParticipant && !covers(person.grant, scope)
  )

  const confirm = () => {
    if (!pending) return
    if (pending.kind === 'all') {
      onShareAll(scope, language)
    } else if (access.people.some((person) => person.email === pending.email)) {
      onPersonChange(pending.email, scope, language)
    } else {
      onAddPerson(pending.email, scope, language)
    }
    setPending(null)
    setQuery('')
  }

  const personRow = (person: AccessPerson) => {
    const granted = covers(person.grant, scope)
    // An internal teammate is already covered by an organisation grant, so
    // offering to "share" with them again would send a pointless email.
    const viaOrganization = person.isInternal && orgOn && !granted
    const viaSeries = person.grant === 'series' && scope === 'meeting'
    const armed = pending?.kind === 'person' && pending.email === person.email

    return (
      <li
        key={person.email}
        className={cn(
          'rounded-[var(--radius-sm)] px-2 py-1.5',
          armed
            ? 'bg-[var(--color-surface-sunken)] shadow-[inset_2px_0_0_var(--color-fg)]'
            : 'hover:bg-[var(--color-surface-hover)]'
        )}
      >
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs text-[var(--color-fg)]">{person.name}</span>
            <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
              {person.email}
            </span>
          </span>

          {granted && person.language ? (
            <Badge tone="neutral" className="shrink-0">
              emailed · {LANGUAGES.find((item) => item.code === person.language)?.label}
            </Badge>
          ) : null}
          {viaOrganization || viaSeries ? (
            <span className="shrink-0 text-2xs text-[var(--color-fg-subtle)]">
              {viaOrganization ? 'via organisation' : 'via series'}
            </span>
          ) : null}

          {viaOrganization || viaSeries || armed ? null : granted ? (
            <Button
              size="xs"
              variant="ghost"
              disabled={busy}
              onClick={() => onPersonChange(person.email, 'none')}
            >
              Remove
            </Button>
          ) : (
            <Button
              size="xs"
              variant="secondary"
              disabled={busy}
              onClick={() => setPending({ kind: 'person', email: person.email, name: person.name })}
            >
              Share
            </Button>
          )}
        </div>

        {armed ? (
          <ConfirmStrip
            label="Emails a link to the notes"
            language={language}
            onLanguageChange={setLanguage}
            onConfirm={confirm}
            onCancel={() => setPending(null)}
            busy={busy}
          />
        ) : null}
      </li>
    )
  }

  const group = (label: string, people: AccessPerson[]) =>
    people.length > 0 ? (
      <>
        <p className="px-2 pt-2 pb-1 text-2xs font-medium tracking-wide text-[var(--color-fg-subtle)] uppercase">
          {label}
        </p>
        {people.map(personRow)}
      </>
    ) : null

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      {/* Span wrapper: Base UI's Tooltip.Trigger clones its child, and cloning
          the Popover.Trigger detaches it from the popover. */}
      <Tooltip content={summary.label}>
        <span className="inline-flex">
          <PopoverTrigger
            disabled={busy}
            aria-label={`${summary.label}. Manage access`}
            className={cn(
              'grid size-6 place-items-center rounded-[var(--radius-sm)] outline-none',
              'transition-colors duration-100 hover:bg-[var(--color-surface-hover)] disabled:opacity-40',
              summary.tone === 'strong'
                ? 'text-[var(--color-fg)]'
                : 'text-[var(--color-fg-subtle)]'
            )}
          >
            <summary.icon className="size-3.5" />
          </PopoverTrigger>
        </span>
      </Tooltip>

      <PopoverContent align="end" className="w-96 max-w-[calc(100vw-2rem)] p-0">
            <div className="border-b border-[var(--color-border)] px-3 py-2">
              <p className="truncate text-xs font-semibold text-[var(--color-fg)]">
                Share “{title}”
              </p>
            </div>
            <div className="space-y-2.5 px-3 py-3">
              {isRecurring ? (
                <SegmentedControl
                  size="sm"
                  className="w-full [&>button]:flex-1"
                  value={scope}
                  onValueChange={setScope}
                  options={[
                    { value: 'meeting', label: 'This meeting' },
                    { value: 'series', label: 'Entire series' },
                  ]}
                />
              ) : null}

              <label className="flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--color-border)] p-2.5 hover:bg-[var(--color-surface-hover)]">
                <Building2 className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-[var(--color-fg)]">
                    Whole organisation
                  </span>
                  <span className="block text-2xs text-[var(--color-fg-subtle)]">
                    {orgOn
                      ? 'Everyone in your org can open this — no email is sent'
                      : 'Only the people listed below'}
                  </span>
                </span>
                <Switch
                  checked={orgOn}
                  disabled={busy || (access.organization === 'series' && scope === 'meeting')}
                  aria-label="Share with the whole organisation"
                  onCheckedChange={(next) => onOrganizationChange(next ? scope : 'none')}
                />
              </label>

              <div className="relative">
                <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search people or add an email…"
                  aria-label="Search people"
                  className="h-8 pl-8 text-xs"
                />
              </div>

              <ul className="-mx-1 max-h-64 overflow-y-auto">
                {group('Internal', internal)}
                {group('External', external)}

                {canAdd ? (
                  <li className="px-2 pt-2">
                    {pending?.kind === 'person' && pending.email === normalized ? (
                      <div className="rounded-[var(--radius-sm)] bg-[var(--color-surface-sunken)] px-2 py-1.5 shadow-[inset_2px_0_0_var(--color-fg)]">
                        <p className="truncate text-xs text-[var(--color-fg)]">{normalized}</p>
                        <ConfirmStrip
                          label="Not on the invite — emails a link"
                          language={language}
                          onLanguageChange={setLanguage}
                          onConfirm={confirm}
                          onCancel={() => setPending(null)}
                          busy={busy}
                        />
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full justify-start"
                        disabled={busy}
                        onClick={() =>
                          setPending({ kind: 'person', email: normalized, name: normalized })
                        }
                      >
                        <Plus className="size-3.5" />
                        Share with {normalized}
                      </Button>
                    )}
                  </li>
                ) : null}

                {internal.length === 0 && external.length === 0 && !canAdd ? (
                  <li className="px-2 py-8 text-center text-2xs text-[var(--color-fg-subtle)]">
                    {normalized
                      ? 'No matches. Type a full email address to add someone.'
                      : 'No participants on this meeting.'}
                  </li>
                ) : null}
              </ul>
            </div>

            <div className="border-t border-[var(--color-border)] bg-[var(--color-surface-sunken)] px-3 py-2">
              {pending?.kind === 'all' ? (
                <ConfirmStrip
                  className="mt-0"
                  label={`Emails ${pending.emails.length} ${pending.emails.length === 1 ? 'attendee' : 'attendees'} a link`}
                  language={language}
                  onLanguageChange={setLanguage}
                  onConfirm={confirm}
                  onCancel={() => setPending(null)}
                  busy={busy}
                />
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-2xs text-[var(--color-fg-subtle)]">
                    {scope === 'series'
                      ? 'Changes apply to the whole series.'
                      : 'Changes apply to this meeting.'}
                  </span>
                  {unshared.length > 0 ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        setPending({ kind: 'all', emails: unshared.map((person) => person.email) })
                      }
                    >
                      <Mail className="size-3.5" />
                      Share with{' '}
                      {unshared.length === 1
                        ? 'the remaining attendee'
                        : `all ${unshared.length} attendees`}
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
      </PopoverContent>
    </Popover>
  )
}
